import {isPlausibleGradYear} from '../components/grad-year/grad-year-util';

/**
 * The redesign's size questions ("Degree you're framing", "When did you graduate?"), built from the current school's diploma size
 * rows (dbo.DocSize via s.docsizes_available). Pure, so the rules are unit-tested on real licensor shapes (size-questions.test.ts):
 *
 * - The degree buttons are the data's size groups (DocSizeName: "Bachelors/Masters", "Doctorate/PhD"...), asked only when two groups
 *   give a different size for some year.
 * - A group asks for the year when its date windows give two or more sizes. A window runs from its StartDate to the end of its
 *   EndDate's month (EndDate is the last month a size applies; 2100 means current, 1775/1776/1860 any older document).
 * - A year picks the window that contains July 1 of that year (diplomas are mostly issued in May or June); a year outside every
 *   window takes the nearest window.
 * - Where the size changed during the typed year (Liberty's Master's: 14" x 17" through May 2018, 13" x 17" from June), the
 *   year question also asks which part of that year: one choice per run of months with one size.
 */

/** The DocSize fields the questions read (ProductDocSize carries more). */
export type DocSizeRow = {
    Id: number;
    DocSizeName: string;
    Doc_Width: number;
    Doc_Height: number;
    StartDate: string | Date;
    EndDate: string | Date;
};

export type SizeWindow = {row: DocSizeRow; start: number; end: number};

export type DegreeGroup = {
    /** The data's DocSizeName, trimmed: the group's identity. */
    key: string;
    /** The button label: "Bachelor's or Master's". */
    label: string;
    windows: SizeWindow[];
    /** The group's windows give two or more sizes. */
    needsYear: boolean;
};

export type SizeQuestions = {
    groups: DegreeGroup[];
    /** Two or more groups give a different size for some year. */
    degreeNeeded: boolean;
};

/** A run of months (1 to 12). */
export type MonthRange = {from: number; to: number};

/** A part of a year with one size: its months and the size row of its first month. */
export type YearPart = MonthRange & {row: DocSizeRow};

export type SizeAnswers = {
    /** The answered degree group's key, or null. */
    degree: string | null;
    /** The year field's text (digits). */
    year: string;
    /** The months picked where the size changed during the year, or null. */
    part?: MonthRange | null;
};

const DAY = 24 * 60 * 60 * 1000;

/** UTC milliseconds of a DocSize date's calendar day ("2024-01-01T00:00:00", a Date, or anything Date parses). */
export function parseDocSizeDate(value: string | Date): number {
    const text = typeof value === 'string' ? value : value instanceof Date ? value.toISOString() : String(value);
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
    if (m) {
        return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    }
    const parsed = new Date(text);
    return Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/** A moment on the windows' scale: the browser's local date and time read as UTC, so a window ends at local midnight. */
export const localMoment = (date: Date = new Date()): number =>
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds());

/** A row's window: StartDate through the last moment of EndDate's month. */
export function windowOf(row: DocSizeRow): SizeWindow {
    const start = parseDocSizeDate(row.StartDate);
    const endMonth = new Date(parseDocSizeDate(row.EndDate));
    const end = Date.UTC(endMonth.getUTCFullYear(), endMonth.getUTCMonth() + 1, 1) - 1;
    return {row, start, end};
}

/** July 1 of a year, the moment the year rule looks up. */
export function july1(year: number): number {
    return Date.UTC(year, 6, 1);
}

/** The window that contains the moment (the most recent start when windows overlap), else the nearest one. */
export function windowAt(windows: SizeWindow[], t: number): SizeWindow {
    const containing = windows.filter(w => w.start <= t && t <= w.end);
    if (containing.length > 0) {
        return containing.reduce((a, b) => (b.start > a.start ? b : a));
    }
    const distance = (w: SizeWindow) => (t < w.start ? w.start - t : t - w.end);
    return windows.reduce((a, b) => (distance(b) < distance(a) ? b : a));
}

export const sizeKey = (row: Pick<DocSizeRow, 'Doc_Width' | 'Doc_Height'>): string => `${Number(row.Doc_Width)}x${Number(row.Doc_Height)}`;

const DEGREE_WORDS: [RegExp, string][] = [
    [/^associates?$/i, "Associate's"],
    [/^bachelors?$/i, "Bachelor's"],
    [/^masters?$/i, "Master's"],
    [/^ph\.?d\.?s?$/i, 'PhD'],
    [/^doctorates?$/i, 'Doctorate'],
    [/^jd$/i, 'JD'],
    [/^postgraduate$/i, 'Postgraduate'],
    [/^postdoctoral$/i, 'Postdoctoral'],
    [/^(hs|high school)$/i, 'High School'],
    [/^prep school$/i, 'Prep School'],
];

/** The degree words of a group name ("Bachelors/Masters" → Bachelor's, Master's), or null when a part is not a known degree. */
export function degreeParts(name: string): string[] | null {
    const parts = (name || '')
        .split('/')
        .map(p => p.trim())
        .filter(p => p !== '');
    if (parts.length === 0) return null;
    const words = parts.map(p => DEGREE_WORDS.find(([re]) => re.test(p))?.[1]);
    return words.every((w): w is string => typeof w === 'string') ? words : null;
}

const joinWords = (words: string[], conjunction: 'or' | 'and'): string => {
    if (words.length <= 1) return words.join('');
    if (words.length === 2) return `${words[0]} ${conjunction} ${words[1]}`;
    return `${words.slice(0, -1).join(', ')} ${conjunction} ${words[words.length - 1]}`;
};

/** A degree button: "Bachelors/Masters" → "Bachelor's or Master's". Names that are not degree lists show as written. */
export function degreeLabel(name: string): string {
    const words = degreeParts(name);
    return words ? joinWords(words, 'or') : (name || '').trim();
}

/** The degrees a size fits, for "Made to fit": "Bachelors/Masters/PhD" → "Bachelor's, Master's and PhD" (PlaceHolderService wording, no article). */
export function degreeWords(name: string): string {
    const words = degreeParts(name);
    return words ? joinWords(words, 'and') : (name || '').trim();
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * The years a window covers, for "Size set": "2024 or later", "before 2024", "2005 to 2023", with the month where the window
 * starts or ends during a year: "before June 2018", "June 2018 or later".
 */
export function windowYears(window: SizeWindow): string {
    const start = new Date(window.start);
    const end = new Date(window.end);
    const [startYear, startMonth] = [start.getUTCFullYear(), start.getUTCMonth()];
    const [endYear, endMonth] = [end.getUTCFullYear(), end.getUTCMonth()];
    const from = startMonth === 0 ? `${startYear}` : `${MONTHS[startMonth]} ${startYear}`;
    if (startYear <= 1860 && endYear >= 2100) return 'any year';
    if (startYear <= 1860) return `before ${endMonth === 11 ? endYear + 1 : `${MONTHS[endMonth + 1]} ${endYear}`}`;
    if (endYear >= 2100) return `${from} or later`;
    if (startYear === endYear) return startMonth === 0 && endMonth === 11 ? `${startYear}` : `${MONTHS[startMonth]} to ${MONTHS[endMonth]} ${startYear}`;
    return `${from} to ${endMonth === 11 ? endYear : `${MONTHS[endMonth]} ${endYear}`}`;
}

/** A part's months: "January to May", "December". */
export function partMonths(part: MonthRange): string {
    return part.from === part.to ? MONTHS[part.from - 1] : `${MONTHS[part.from - 1]} to ${MONTHS[part.to - 1]}`;
}

/** The months most schools award degrees in, by the name graduates give them. */
const SEASONS: [number, string][] = [
    [5, 'spring'],
    [8, 'summer'],
    [12, 'fall'],
];

/** The graduations a part holds: "Spring", "Summer or fall"; '' when it holds none of May, August and December. */
export function partSeasons(part: MonthRange): string {
    const text = joinWords(
        SEASONS.filter(([month]) => part.from <= month && month <= part.to).map(([, name]) => name),
        'or',
    );
    return text.charAt(0).toUpperCase() + text.slice(1);
}

/** A part's name for its button and the step's pick: its graduations, else its months. */
export const partName = (part: MonthRange): string => partSeasons(part) || partMonths(part);

/** The month the size changed in a two-part year ("June"), or null when it changed more than once. */
export const changeMonth = (parts: MonthRange[]): string | null => (parts.length === 2 ? MONTHS[parts[1].from - 1] : null);

/** The size question groups for the current school's rows, in the rows' order (s.docsizes_available lists Bachelor's first). */
export function buildSizeQuestions(rows: DocSizeRow[]): SizeQuestions {
    const byKey = new Map<string, SizeWindow[]>();
    for (const row of rows ?? []) {
        const key = (row.DocSizeName || '').trim();
        if (!byKey.has(key)) byKey.set(key, []);
        byKey.get(key)!.push(windowOf(row));
    }
    const groups: DegreeGroup[] = [...byKey.entries()].map(([key, windows]) => ({
        key,
        label: degreeLabel(key),
        windows,
        needsYear: new Set(windows.map(w => sizeKey(w.row))).size > 1,
    }));
    return {groups, degreeNeeded: groupsDiffer(groups)};
}

/** Two groups give a different size at some moment: checked at every window edge and between them (a gap switches at its middle). */
function groupsDiffer(groups: DegreeGroup[]): boolean {
    if (groups.length < 2) return false;
    const edges = [...new Set(groups.flatMap(g => g.windows.flatMap(w => [w.start, w.end + 1])))].sort((a, b) => a - b);
    const moments = [edges[0] - DAY, ...edges];
    for (let i = 0; i < edges.length - 1; i++) {
        moments.push(Math.floor((edges[i] + edges[i + 1]) / 2));
    }
    return moments.some(t => new Set(groups.map(g => sizeKey(windowAt(g.windows, t).row))).size > 1);
}

/** A year the size question accepts: four digits, the graduation year's own plausibility rule (1000 to 3000). */
export function isAnsweredYear(text: string): boolean {
    return /^\d{4}$/.test(text || '') && isPlausibleGradYear(text);
}

export function groupByKey(q: SizeQuestions, key: string | null): DegreeGroup | undefined {
    return key == null ? undefined : q.groups.find(g => g.key === key);
}

/** The group a row belongs to. */
export function groupOfRow(q: SizeQuestions, rowId: number): DegreeGroup | undefined {
    return q.groups.find(g => g.windows.some(w => w.row.Id === rowId));
}

/** The year question shows: for the answered degree, when its sizes change with the year; before that, when any group's do. */
export function yearShown(q: SizeQuestions, degree: string | null): boolean {
    const group = groupByKey(q, degree);
    return group ? group.needsYear : q.groups.some(g => g.needsYear);
}

/**
 * The group the answers take their size from: the answered degree, undefined while the degree question is open, and with no degree
 * question the current row's group (all groups give the same size then).
 */
export function sizingGroup(q: SizeQuestions, degree: string | null, currentRowId: number): DegreeGroup | undefined {
    const group = groupByKey(q, degree);
    if (group || q.degreeNeeded) return group;
    return groupOfRow(q, currentRowId) ?? q.groups[0];
}

/** A year's runs of months with one size, month by month (every DocSize date is the 1st of a month). One part: the size held all year. */
export function yearParts(windows: SizeWindow[], year: number): YearPart[] {
    const parts: YearPart[] = [];
    for (let month = 1; month <= 12; month++) {
        const row = windowAt(windows, Date.UTC(year, month - 1, 1)).row;
        const last = parts[parts.length - 1];
        if (last && sizeKey(last.row) === sizeKey(row)) {
            last.to = month;
        } else {
            parts.push({from: month, to: month, row});
        }
    }
    return parts;
}

/** The parts the year question asks the shopper to pick from: two or more where the size changed during the typed year, else none. */
export function partsToPick(q: SizeQuestions, answers: SizeAnswers, currentRowId: number): YearPart[] {
    const group = sizingGroup(q, answers.degree, currentRowId);
    if (!group?.needsYear || !isAnsweredYear(answers.year)) return [];
    const parts = yearParts(group.windows, Number(answers.year));
    return parts.length > 1 ? parts : [];
}

/** The part a pick falls in. A pick spanning two parts decides nothing (another degree's size changed in a month the pick holds). */
export function partOf(parts: YearPart[], picked: MonthRange | null | undefined): YearPart | undefined {
    return picked ? parts.find(p => p.from <= picked.from && picked.to <= p.to) : undefined;
}

/** The moment a year answer stands for: the picked part's first day, else July 1. */
export const yearMoment = (year: number, part?: MonthRange): number => (part ? Date.UTC(year, part.from - 1, 1) : july1(year));

/** The year is answered: four digits, and where the size changed during that year, the part of it. */
export function yearAnswered(q: SizeQuestions, answers: SizeAnswers, currentRowId: number): boolean {
    if (!isAnsweredYear(answers.year)) return false;
    const parts = partsToPick(q, answers, currentRowId);
    return parts.length === 0 || partOf(parts, answers.part) !== undefined;
}

/** The questions still open, in step order. currentRowId names the group when no degree question is asked (any group without it). */
export function openQuestions(q: SizeQuestions, answers: SizeAnswers, currentRowId = -1): ('degree' | 'year')[] {
    const open: ('degree' | 'year')[] = [];
    if (q.degreeNeeded && !groupByKey(q, answers.degree)) open.push('degree');
    if (yearShown(q, answers.degree) && !yearAnswered(q, answers, currentRowId)) open.push('year');
    return open;
}

/**
 * The size the answers set, or null while the degree they need is open. A degree answered before the year takes its window for
 * today (the price follows the degree at once); a year applies the July 1 rule until the part of a year with two sizes is picked.
 * With no degree question the current row's group answers it (all groups give the same size).
 */
export function rowForAnswers(q: SizeQuestions, answers: SizeAnswers, currentRowId: number, now: number): DocSizeRow | null {
    const group = sizingGroup(q, answers.degree, currentRowId);
    if (!group) return null;
    if (!isAnsweredYear(answers.year)) return windowAt(group.windows, now).row;
    const part = partOf(partsToPick(q, answers, currentRowId), answers.part);
    return windowAt(group.windows, yearMoment(Number(answers.year), part)).row;
}
